<template>
  <popup @close="$emit('close')">
    <div slot="popupMainContent" class="bottom-bar-configurator">
      <h3 class="mb-4">Bottom Bar Configuration</h3>

      <!-- Master toggle -->
      <label
          :for="'alwaysHide' + _uid"
          class="container d-inline-block mb-3"
          style="padding-left: 5px !important"
      >
        <input
            :id="'alwaysHide' + _uid"
            v-model="localConfig.alwaysHideBottomBar"
            type="checkbox"
            @change="save"
        >
        <span class="checkmark"/>
        <div class="ml-4">Always hide bottom bar</div>
      </label>

      <!-- Per-item toggles -->
      <transition name="fade">
        <div v-if="!localConfig.alwaysHideBottomBar" class="item-toggles mt-2 mb-3">
          <h4 class="mb-2">Visible items</h4>
          <div class="row">
            <div
                v-for="item in items"
                :key="item.key"
                class="col-12 col-md-6">
              <label
                  :for="item.key + _uid"
                  class="container d-inline-block mb-2 toggle-item"
                  style="padding-left: 5px !important"
              >
                <input
                    :id="item.key + _uid"
                    v-model="localConfig[item.key]"
                    type="checkbox"
                    @change="save"
                >
                <span class="checkmark"/>
                <div class="ml-4">{{ item.label }}</div>
              </label>
            </div>
          </div>
        </div>
      </transition>

      <!-- Live Preview -->
      <div class="preview-section mt-3">
        <h4 class="mb-2">Preview</h4>
        <div class="bottom-bar-preview d-flex align-items-center">
          <template v-if="!localConfig.alwaysHideBottomBar">
            <div v-if="localConfig.showLogo" class="preview-item preview-logo">
              <icon size="0.8" type="image"/>
            </div>
            <div v-if="localConfig.showProjectName" class="preview-item form-name-preview">
              <span class="preview-text">Form Name</span>
            </div>
            <div class="flex-grow-1"/>
            <div v-if="localConfig.showUserDisplay" class="preview-item preview-icon-item">
              <icon size="0.8" type="user"/>
              <span class="preview-text ml-1">User</span>
            </div>
            <div v-if="localConfig.showLanguageSelector" class="preview-item preview-lang">
              EN
            </div>
            <div v-if="localConfig.showSlideOutMenu" class="preview-item preview-icon-item">
              <icon size="0.85" type="sfx-icon-horizontal-more"/>
            </div>
            <div v-if="localConfig.showHomeButton" class="preview-item preview-icon-item">
              <icon size="0.9" type="sfx-icon-home"/>
            </div>
            <div v-if="localConfig.showPrevButton" class="preview-item preview-icon-item">
              <icon size="0.7" type="sfx-icon-arrow-left"/>
            </div>
            <div v-if="localConfig.showNextButton" class="preview-item preview-icon-item">
              <icon size="0.7" type="sfx-icon-arrow-right"/>
            </div>
            <div v-if="allItemsHidden" class="preview-empty">
              <span class="preview-text muted">(all items hidden)</span>
            </div>
          </template>
          <template v-else>
            <div class="preview-hidden-label w-100 text-center">
              <span class="preview-text muted">bottom bar hidden</span>
            </div>
          </template>
        </div>
      </div>
    </div>
  </popup>
</template>

<script>
import Popup from "@/components/Popup";
import Icon from "@/components/Icon";

const ITEMS = [
  {key: 'showLogo', label: 'Logo'},
  {key: 'showProjectName', label: 'Project Name'},
  {key: 'showUserDisplay', label: 'User Display'},
  {key: 'showLanguageSelector', label: 'Language Selector'},
  {key: 'showSlideOutMenu', label: 'Slide-out Menu'},
  {key: 'showHomeButton', label: 'Home Button'},
  {key: 'showPrevButton', label: 'Prev Button'},
  {key: 'showNextButton', label: 'Next Button'},
];

function buildDefaults(bottomBar) {
  const cfg = {};
  ITEMS.forEach(({key}) => {
    cfg[key] = bottomBar && bottomBar[key] !== undefined ? bottomBar[key] : true;
  });
  return cfg;
}

export default {
  name: "BottomBarConfigurator",
  components: {Popup, Icon},

  props: {
    formSettings: {type: Object, required: true},
  },

  data() {
    return {
      items: ITEMS,
      localConfig: {
        alwaysHideBottomBar: !!this.formSettings.alwaysHideBottomBar,
        ...buildDefaults(this.formSettings.bottomBar),
      },
    };
  },

  computed: {
    allItemsHidden() {
      return ITEMS.every(({key}) => !this.localConfig[key]);
    },
  },

  methods: {
    save() {
      this.$set(this.formSettings, 'alwaysHideBottomBar', this.localConfig.alwaysHideBottomBar);
      const bottomBar = {};
      ITEMS.forEach(({key}) => {
        bottomBar[key] = this.localConfig[key];
      });
      this.$set(this.formSettings, 'bottomBar', bottomBar);
    },
  },
};
</script>

<style scoped>
.bottom-bar-configurator {
  min-width: 420px;
}

h4 {
  font-size: 0.85rem;
  opacity: 0.65;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.toggle-item {
  width: 50%;
}

/* Preview */
.preview-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 14px;
}

.bottom-bar-preview {
  background: #1a1a2a;
  border-radius: 6px;
  padding: 8px 12px;
  min-height: 44px;
  flex-wrap: nowrap;
  overflow: hidden;
}

.preview-item {
  display: flex;
  align-items: center;
  padding: 0 7px;
  white-space: nowrap;
  opacity: 0.85;
  flex-shrink: 0;
}

.preview-icon-item {
  opacity: 0.75;
}

.preview-logo {
  background: rgba(255,255,255,0.15);
  border-radius: 3px;
  width: 32px;
  height: 28px;
  justify-content: center;
  margin-right: 4px;
}

.preview-lang {
  font-size: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 4px;
  padding: 2px 6px;
  margin: 0 4px;
}

.form-name-preview {
  font-size: 0.8rem;
  font-weight: 600;
}

.preview-text {
  font-size: 0.78rem;
  color: #ccc;
}

.preview-text.muted {
  opacity: 0.4;
}

.preview-empty,
.preview-hidden-label {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.item-toggles {
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 6px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

